import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider } from "@mui/material";

import { ServiceRequest } from "../../../types/service.type";
import { getServicePageRoute } from "../../../../../routers/routeUtils";
import { useServiceStatusFlags } from "../../../hooks/useServiceStatusFlags";
import { useServiceRequestActions } from "../../../hooks/useServiceRequestActions";
import { useWithdrawContract } from "../../../api/mutations/useWithdrawContract";

interface RequesterActionsProps {
  serviceRequest: ServiceRequest;
  isContractPaid: boolean;
  onOpenModal: (config: { title: string; subTitle?: string; onConfirm: () => void }) => void;
  onCloseModal: () => void;
}

export const RequesterActions = ({
  serviceRequest,
  isContractPaid,
  onOpenModal,
  onCloseModal,
}: RequesterActionsProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });
  const navigate = useNavigate();
  const { isCompleted, isRejected, isInitiated, isNegotiated, isContracted } = useServiceStatusFlags(serviceRequest);

  const { handleWithdrawRequest, handleCreateContract, isResponding, isRequesting } =
    useServiceRequestActions(serviceRequest);

  const { mutate: withdrawContract, isPending: isWithdrawingContract } = useWithdrawContract(
    serviceRequest.contractId!,
    serviceRequest.id,
    { onSuccess: onCloseModal }
  );

  const handleWithdrawContract = () => {
    if (!serviceRequest.contractId) return;
    withdrawContract();
  };

  const handleWithdrawContractWithModal = () => {
    if (!serviceRequest.contractId) return;
    onOpenModal({
      title: isContractPaid ? t("areYouSureToEndContract") : t("areYouSureToWithdrawThisRequest"),
      subTitle: isContractPaid
        ? t("onceEndedThisContractThisActionCannotBeUndone")
        : t("onceRejectedThisContractThisActionCannotBeUndone"),
      onConfirm: handleWithdrawContract,
    });
  };

  const handleRequestService = () => {
    navigate(getServicePageRoute(serviceRequest.service.id, serviceRequest.service.type));
  };

  const handleWithdrawRequestWithModal = () => {
    onOpenModal({
      title: t("areYouSureToWithdrawThisRequest"),
      onConfirm: handleWithdrawRequest,
    });
  };

  if (isNegotiated) {
    return (
      <>
        <Divider />
        <Button variant="outlined-md" onClick={handleWithdrawContractWithModal} disabled={isWithdrawingContract}>
          {t("withdrawContract")}
        </Button>
      </>
    );
  }

  if (isContracted) {
    return (
      <>
        <Divider />
        <Button variant="outlined-md" onClick={handleWithdrawContractWithModal} disabled={isWithdrawingContract}>
          {isContractPaid ? t("endContract") : t("withdrawContract")}
        </Button>
      </>
    );
  }

  if (isInitiated) {
    return (
      <>
        <Divider />
        <Box sx={styles.container}>
          <Button variant="primary-md" onClick={handleCreateContract}>
            {t("createContract")}
          </Button>
          <Button variant="outlined-sm" onClick={handleWithdrawRequestWithModal} disabled={isResponding}>
            {t("withdrawRequest")}
          </Button>
        </Box>
      </>
    );
  }

  if (isCompleted) {
    return (
      <>
        <Divider />
        <Button variant="primary-md" onClick={handleRequestService} disabled={isRequesting}>
          {t("requestThisService")}
        </Button>
      </>
    );
  }

  if (isRejected) {
    return (
      <>
        <Divider />
        <Button variant="primary-md" onClick={handleRequestService} disabled={isRequesting}>
          {t("requestThisService")}
        </Button>
      </>
    );
  }

  return null;
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
};
